// src/features/owner/components/DataTable.tsx — generic, sortable, empty-aware table.
import { useMemo, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { Icon, type IconName } from '@/icons';
import { EmptyState } from './EmptyState';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  sortBy?: (row: T) => string | number;
  align?: 'left' | 'right';
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fontSize.sm};
`;
const Th = styled.th<{ $sortable: boolean }>`
  text-align: left;
  padding: 10px 12px;
  color: ${({ theme }) => theme.color.textMuted};
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-weight: 600;
`;
const HeadBtn = styled.button`
  background: none;
  border: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
`;
const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textPrimary};
`;

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyIcon: IconName;
  emptyMessage: string;
}

export function DataTable<T>({ columns, rows, rowKey, emptyIcon, emptyMessage }: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [asc, setAsc] = useState(true);

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortBy) return rows;
    const out = [...rows].sort((a, b) => {
      const av = col.sortBy!(a), bv = col.sortBy!(b);
      return av < bv ? -1 : av > bv ? 1 : 0;
    });
    return asc ? out : out.reverse();
  }, [rows, columns, sortKey, asc]);

  if (rows.length === 0) return <EmptyState icon={emptyIcon} message={emptyMessage} />;

  return (
    <Table>
      <thead>
        <tr>
          {columns.map((c) => (
            <Th
              key={c.key}
              $sortable={!!c.sortBy}
              aria-sort={sortKey === c.key ? (asc ? 'ascending' : 'descending') : undefined}
            >
              {c.sortBy ? (
                <HeadBtn
                  onClick={() => {
                    if (sortKey === c.key) setAsc(!asc);
                    else { setSortKey(c.key); setAsc(true); }
                  }}
                >
                  {c.header}
                  <Icon name="chevron-down" size={12} />
                </HeadBtn>
              ) : (
                c.header
              )}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={rowKey(r)}>
            {columns.map((c) => (
              <Td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                {c.render(r)}
              </Td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
